/**
 * Normalisation des query params discovery /recherche (legacy SEO + SPA).
 * Edge : netlify/edge-functions/lib/discovery-search-url.ts (metiers-seo.json).
 */
import { METIERS_DATA } from '../pages/MetierLanding/metiersData';

const LEGACY_VILLE_DEFAULT_PROFESSION = 'trainers';

export function discoverySearchHasStructuredFilter(
  searchOrParams: string | URLSearchParams
): boolean {
  const p =
    typeof searchOrParams === 'string' ? new URLSearchParams(searchOrParams) : searchOrParams;
  return Boolean(
    p.get('profession') ||
    p.get('serviceTypeId') ||
    p.get('service_type_id') ||
    p.get('serviceId') ||
    p.get('service_id') ||
    p.get('speciesId') ||
    p.get('species_id')
  );
}

export function discoverySearchHasValidCoords(params: URLSearchParams): boolean {
  const lat = parseFloat(params.get('lat') || '');
  const lon = parseFloat(params.get('lon') || '');
  return Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0;
}

/**
 * Params exploitables par GET /search/pros (coords géocodées côté client ou edge).
 * Une localisation suffit : le métier et la prestation sont facultatifs.
 */
export function discoverySearchHasMinimumFilters(params: URLSearchParams): boolean {
  if (params.get('mode') === 'ask') {
    if (!params.get('ask')?.trim()) return false;
    return discoverySearchHasStructuredFilter(params);
  }
  if (!params.get('address')?.trim()) return false;
  return discoverySearchHasValidCoords(params);
}

export function hasDiscoveryGeo(params: URLSearchParams): boolean {
  return discoverySearchHasValidCoords(params);
}

/** La page /recherche doit rester affichée (legacy ou discovery), sans renvoi vers /. */
export function discoverySearchShouldRenderPage(params: URLSearchParams): boolean {
  if (!params.get('address')?.trim()) return false;
  if (params.get('mode') === 'ask') return discoverySearchHasStructuredFilter(params);
  return true;
}

function resolveProfessionFromMetierParam(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const fromLanding = METIERS_DATA[trimmed]?.profession;
  if (fromLanding) return fromLanding;
  const asProfession = Object.values(METIERS_DATA).find(m => m.profession === trimmed);
  if (asProfession?.profession) return asProfession.profession;
  return null;
}

/**
 * Mappe ville/metier legacy → address/profession discovery.
 * Retourne une copie ; `changed` si l’URL canonique diffère.
 */
export function normalizeLegacyDiscoverySearchParams(input: URLSearchParams): {
  params: URLSearchParams;
  changed: boolean;
} {
  const params = new URLSearchParams(input);
  let changed = false;
  const hadLegacyVille = input.has('ville');

  if (params.get('ville')?.trim() && !params.get('address')?.trim()) {
    params.set('address', params.get('ville')!.trim());
    params.delete('ville');
    changed = true;
  }
  if (params.get('city')?.trim() && !params.get('address')?.trim()) {
    params.set('address', params.get('city')!.trim());
    params.delete('city');
    changed = true;
  }

  const metierRaw = params.get('metier') || params.get('metierSlug');
  if (metierRaw && !params.get('profession')) {
    const profession = resolveProfessionFromMetierParam(metierRaw);
    if (profession) {
      params.set('profession', profession);
      params.delete('metier');
      params.delete('metierSlug');
      changed = true;
    }
  }

  if (hadLegacyVille && !params.get('profession')?.trim()) {
    params.set('profession', LEGACY_VILLE_DEFAULT_PROFESSION);
    changed = true;
  }

  if (discoverySearchShouldRenderPage(params) && !params.get('radius')) {
    params.set('radius', '25');
    changed = true;
  }
  if (discoverySearchShouldRenderPage(params) && !params.get('sort')) {
    params.set('sort', 'relevance');
    changed = true;
  }

  return { params, changed };
}

export function legacyDiscoverySearchQueryChanged(
  before: URLSearchParams,
  after: URLSearchParams
): boolean {
  return before.toString() !== after.toString();
}

/** Check if discovery search is in Ask mode */
export function discoverySearchIsAskMode(params: URLSearchParams): boolean {
  return params.get('mode') === 'ask';
}
